import React, { FC, useEffect, useMemo, useRef } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { CustomSelectField } from "../../../../pro/applicationForm/customSelect";
import { CustomFieldText } from "../../../../../components/fields/CustomFieldText/CustomFieldText";
import { SkuPdpLinkEditor } from "../../../../../components/SkuLinks/SkuPdpLinkEditor";
import { INTERVAL_LABELS, PDP_SCORE_INTERVALS } from "../../../../../utils/plans";
import { brandOptionLabel, usePdpUrlBrand } from "../../../productsV2/productModal/ProductPdpUrlsPanel";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { FIELD_FOCUS, btnClass } from "../components/ui";
import { productUrlHref } from "../pageModel";
import { addPageUrlError } from "./pageModel";
import type { AddPageValues } from "./useProductPages";

/**
 * "Add a product page" — the modal's Add tab (MODAL/ProductPdpUrlsPanel.tsx:
 * 735-803, `handleAdd` :241-280) as the handoff's inline form (README:154-160).
 *
 * The fields are the order form's register, unchanged (a locked rule):
 * `CustomFieldText` (the primitive `InputField` wraps, with InputField's own
 * props: fullWidth, required) and `CustomSelectField` (pages/pro/applicationForm),
 * inside a small Formik wrapper; SKUs are the modal's `SkuPdpLinkEditor`. No
 * new control is drawn. Their `CustomFieldLabel` is a Typography, not a
 * <label>, so each control carries its name as an aria-label — else a
 * select is named by its value.
 *
 * - Page URL: prefilled with the product URL only when it is a real http(s)
 *   link (`productUrlHref`, the header's link rule, and its normalised href:
 *   "brand.com/p" prefills https://brand.com/p; a relative path or a plain
 *   product name prefills nothing). Required (the modal's "Please enter a PDP
 *   URL before saving.", here a field error), and it must be an http:// or
 *   https:// URL, with the Edit dialog's message (`addPageUrlError`).
 * - Owner brand: `usePdpUrlBrand` (:133-144), shown only outside a workspace
 *   (:753), exactly as the modal. The hook stays the source of truth; the
 *   Formik field mirrors it so the register's select can draw it.
 * - Score refresh: "Default" is the empty value, and the POST leaves
 *   `score_interval` out (:262), so the API picks weekly or the plan cap.
 *   The shared select draws an empty value at 12% opacity, as a placeholder
 *   (CustomFieldSelect `opacity: $val ? 1 : 0.12`); Default is a real choice,
 *   so its input carries `!opacity-100` (Select hands `inputProps.className`
 *   to the value box). The Owner's "Select a brand" is a placeholder and keeps
 *   the register's look.
 * - Focus: the fields sit in FIELD_FOCUS (ui.tsx), since the register's
 *   fields draw no focus state of their own.
 * - The POST, its body and its toasts are useProductPages' `add`.
 */

interface FormValues {
  pdp_url: string;
  owner: string;
  score_interval: string;
  skus: string[];
}

const INTERVAL_OPTIONS = PDP_SCORE_INTERVALS.map((i) => ({ label: INTERVAL_LABELS[i], value: i }));

const HELPER = { fontSize: 11.5, marginTop: 6 } as const;
const URL_HELP_ID = "add-product-page-url-help";

/** Mirrors the hook's pick into Formik (the hook auto-picks a single brand, :139-141). */
const OwnerSync: FC<{ picked: string }> = ({ picked }) => {
  const { setFieldValue } = useFormikContext<FormValues>();
  useEffect(() => {
    setFieldValue("owner", picked);
  }, [picked, setFieldValue]);
  return null;
};

interface Props {
  product: ISingleProduct;
  /** The section closes the form and focuses the control that opened it. */
  onCancel: () => void;
  /** The POST. Resolves to an inline error, or null once the page is added and listed. */
  onAdd: (values: AddPageValues) => Promise<string | null>;
  /** After a successful add: the section closes the form and focuses the new page's tab (or block). */
  onAdded: () => void;
}

export const AddPageForm: FC<Props> = ({ product, onCancel, onAdd, onAdded }) => {
  const pdpBrand = usePdpUrlBrand(product.brands);
  const { workspaceBrandId, options, picked, setPicked } = pdpBrand;
  const showOwner = !workspaceBrandId;
  const prefill = productUrlHref(product.url) ?? "";
  const ownerOptions = useMemo(() => options.map((b) => ({ label: brandOptionLabel(b), value: String(b.id) })), [options]);

  // The button that opened the form may have left with the block it sat in
  // (the empty block's Add a page → the card), so focus lands on the first
  // field, not the body. Closing hands it back: the section focuses the
  // opener on Cancel, the new page's tab (or block) after an add (#13).
  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    wrapRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, []);

  // The hook's own formula (:142), read from the form's mirror of it.
  const brandIdOf = (values: FormValues): number | null =>
    workspaceBrandId || (values.owner ? Number(values.owner) : null);

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    const urlError = addPageUrlError(values.pdp_url);
    if (urlError) errors.pdp_url = urlError;
    if (!brandIdOf(values)) {
      errors.owner = options.length
        ? "Select the brand this page belongs to."
        : "No brand on this product yet — set the brand first.";
    }
    return errors;
  };

  return (
    <Formik<FormValues>
      initialValues={{ pdp_url: prefill, owner: picked, score_interval: "", skus: [] }}
      validate={validate}
      onSubmit={async (values, helpers) => {
        helpers.setStatus(null);
        const error = await onAdd({
          url: values.pdp_url,
          brandId: brandIdOf(values) as number,
          interval: values.score_interval,
          skus: values.skus,
        });
        if (error) helpers.setStatus(error);
        else onAdded();
      }}
    >
      {({ values, setFieldValue, isSubmitting, status }) => (
        <div ref={wrapRef} className="min-w-0">
        <Form
          noValidate
          aria-label="Add a product page"
          className="m-0 flex flex-col gap-4 rounded-xl border border-border-token px-4 py-4 sm:px-5 sm:py-[18px] min-w-0"
        >
          <OwnerSync picked={picked} />
          <div className="font-CircularStdBlack text-text-1" style={{ fontSize: 13.5 }}>
            Add a product page
          </div>
          <div
            className={`grid gap-x-5 gap-y-4 min-w-0 ${FIELD_FOCUS}`}
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))" }}
          >
            <div className="min-w-0" style={{ gridColumn: "1 / -1" }}>
              <CustomFieldText
                name="pdp_url"
                label="Page URL"
                type="url"
                required
                fullWidth
                placeholder="https://retailer.com/p/product…"
                disabled={isSubmitting}
                inputProps={{ "aria-label": "Page URL", "aria-describedby": prefill ? URL_HELP_ID : undefined }}
              />
              {prefill && (
                <div id={URL_HELP_ID} className="text-text-2" style={HELPER}>
                  Prefilled with the product URL.
                </div>
              )}
            </div>
            {showOwner && (
              <div className="min-w-0">
                <CustomSelectField
                  name="owner"
                  label="Owner brand"
                  placeholder="Select a brand"
                  options={ownerOptions}
                  disabled={isSubmitting}
                  onChange={(e: any) => setPicked(String(e.target.value ?? ""))}
                  inputProps={{ "aria-label": "Owner brand" }}
                />
                {/* The modal's helper for this field (:762). */}
                <div className="text-text-2" style={HELPER}>
                  Whose page this is — the retailer for a retail listing, the supplier for its own site.
                </div>
              </div>
            )}
            <div className="min-w-0">
              <CustomSelectField
                name="score_interval"
                label="PDP analysis interval"
                placeholder="Default"
                required={false}
                options={INTERVAL_OPTIONS}
                disabled={isSubmitting}
                inputProps={{ "aria-label": "PDP analysis interval", className: "!opacity-100" }}
              />
              {/* The modal's helper (:781), worded for the Default option. */}
              <div className="text-text-2" style={HELPER}>
                Default = weekly, or the brand&apos;s plan cap if slower. Cannot exceed the cap. The day it runs on
                is set on the page afterwards.
              </div>
            </div>
            <div className="min-w-0">
              <SkuPdpLinkEditor
                value={values.skus}
                onChange={(skus) => setFieldValue("skus", skus)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          {status && (
            <div role="alert" className="text-down" style={{ fontSize: 12.5 }}>
              {status}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-text-2" style={{ flex: "1 1 240px", fontSize: 11.5 }}>
              The first score is queued as soon as the page is added.
            </span>
            {/* mousedown keeps focus where it is: a blur of an empty Page URL
                shows its error, which pushed Cancel 20px down between
                mousedown and mouseup, so the click missed it (it took two). */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onCancel}
              disabled={isSubmitting}
              className={btnClass("ghost", { disabled: isSubmitting })}
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className={btnClass("primary", { disabled: isSubmitting })}>
              {isSubmitting ? "Adding…" : "Add page"}
            </button>
          </div>
        </Form>
        </div>
      )}
    </Formik>
  );
};

